/*
    Copyright 2020 Lauren Gyldenlove.
    This file is part of Duelmaster.

    Duelmaster is free software: you can redistribute it and/or modify
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Duelmaster is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with Foobar.  If not, see <https://www.gnu.org/licenses/>.
 */

import DamageType from './DamageType';

export interface Entry {
    damage: number;
    type: DamageType;
}

export interface Model {
    lineItems: Array<Entry>
}

/**
 * Create an empty ledger.
 */
export function create(): Model {
    return {
        "lineItems": []
    }
}

function addDamage(ledger: Model, damage: number, type: DamageType): Model {
    return {
        "lineItems": ledger.lineItems.concat({ "damage": damage, "type": type })
    }
}


/**
 * Add battle damage to the ledger.
 * @param ledger The ledger to add the line item to
 * @param damage The amount of damage to apply
 */
export function addBattleDamage (ledger: Model, damage: number): Model {
    return addDamage(ledger, damage, DamageType.BATTLE);
}

/**
 * Add an effect type entry into the ledger.
 * @param ledger The ledger to add the line item to
 * @param damage The amount of damage to add
 */
export function addEffectDamage (ledger: Model, damage: number): Model {
    return addDamage(ledger, damage, DamageType.EFFECT);
}

/**
 * Retrieves the last entry within the ledger.
 * @param ledger The ledger to query
 */
export function getLastDamage (ledger: Model): number {
    return ledger.lineItems.length > 0 ? ledger.lineItems.slice(-1)[0].damage: 0;
}

/**
 * Sums the total damage in a ledger
 * @param ledger The ledger to query
 */
export function getTotalDamage(ledger: Model): number {
    return ledger.lineItems.reduce((sum: number, current: Entry) => sum + current.damage, 0);
}

/**
 * Adds an entry reversing another entry.
 * @param ledger The ledger
 * @param index The index of the line item to reverse
 */
export function reverse(ledger: Model, index: number): Model {
    return addDamage(ledger, ledger.lineItems[index].damage * -1, DamageType.REVERSAL_ENTRY);
}

/**
 * Reverse a series of damage entries within the ledger.
 * @param ledger The ledger to adjust
 * @param elements The index of the elements to reverse
 */
export function reverseSet(ledger: Model, elements: number[]): Model {
    return {
        "lineItems": ledger.lineItems.concat(elements.map((entry): Entry => { 
            return { "damage": ledger.lineItems[entry].damage * -1, "type": DamageType.REVERSAL_ENTRY };
        }))
    }
}
